import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "../contexts/LangContext";
import { heroImages } from "../data/cases";
import FadeIn from "../components/FadeIn";
import SEO from "../components/SEO";
import { seoByPath } from "../lib/seo";
import Img from "../components/Img";

const Privat = () => {
    const { t } = useLang();

    const values = [
        { title: t.privat.v1Title, text: t.privat.v1Text },
        { title: t.privat.v2Title, text: t.privat.v2Text },
        { title: t.privat.v3Title, text: t.privat.v3Text },
        { title: t.privat.v4Title, text: t.privat.v4Text },
    ];
    const steps = [
        { title: t.privat.step1, text: t.privat.step1Text },
        { title: t.privat.step2, text: t.privat.step2Text },
        { title: t.privat.step3, text: t.privat.step3Text },
        { title: t.privat.step4, text: t.privat.step4Text },
    ];

    return (
        <main data-testid="page-privat" className="bg-white text-[#111111]">
            <SEO {...seoByPath["/privat"]} path="/privat" />
            {/* Hero */}
            <section className="pt-40 md:pt-56 pb-20 md:pb-32">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-6">
                    <FadeIn className="col-span-12 md:col-span-4">
                        <div className="text-[11px] tracking-luxury uppercase text-[#888]">
                            {t.privat.eyebrow}
                        </div>
                    </FadeIn>
                    <FadeIn delay={0.1} className="col-span-12 md:col-span-8">
                        <h1 className="font-serif font-light text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight whitespace-pre-line">
                            {t.privat.title}
                        </h1>
                        <p className="mt-10 max-w-xl text-base md:text-lg text-[#555] leading-relaxed md:leading-relaxed">
                            {t.privat.intro}
                        </p>
                    </FadeIn>
                </div>
            </section>

            {/* Editorial image */}
            <section className="pb-24 md:pb-32">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12">
                    <FadeIn>
                        <div className="aspect-[16/9] overflow-hidden bg-[#EBE8E3]">
                            <Img
                                priority
                                src={heroImages.kystKitchenMain}
                                alt="Privat bygherre"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </FadeIn>
                </div>
            </section>

            {/* Values */}
            <section className="py-24 md:py-32 bg-[#F9F8F6]">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12">
                    <FadeIn className="mb-16 md:mb-24">
                        <div className="text-[11px] tracking-luxury uppercase text-[#888] mb-3">
                            — 01
                        </div>
                        <h2 className="font-serif font-light text-4xl md:text-6xl leading-[1] tracking-tight max-w-3xl">
                            {t.privat.valuesTitle}
                        </h2>
                    </FadeIn>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-16">
                        {values.map((v, i) => (
                            <FadeIn
                                key={i}
                                delay={(i % 2) * 0.1}
                                className="border-t border-[#D9D4CD] pt-8"
                            >
                                <div className="flex items-baseline gap-6">
                                    <span className="editorial-number text-lg not-italic">
                                        0{i + 1}
                                    </span>
                                    <div className="flex-1">
                                        <h3 className="font-serif text-2xl md:text-3xl mb-3">
                                            {v.title}
                                        </h3>
                                        <p className="text-sm md:text-base text-[#555] leading-relaxed md:leading-relaxed max-w-md">
                                            {v.text}
                                        </p>
                                    </div>
                                </div>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            {/* Process */}
            <section className="py-24 md:py-32">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12">
                    <FadeIn className="mb-16 md:mb-24">
                        <div className="text-[11px] tracking-luxury uppercase text-[#888] mb-3">
                            — 02
                        </div>
                        <h2 className="font-serif font-light text-4xl md:text-6xl leading-[1] tracking-tight max-w-3xl">
                            {t.privat.processTitle}
                        </h2>
                    </FadeIn>
                    <div className="space-y-0">
                        {steps.map((s, i) => (
                            <FadeIn
                                key={i}
                                delay={i * 0.05}
                                className="grid grid-cols-12 gap-6 py-8 md:py-10 border-t border-[#EAE8E4] group hover:bg-[#F9F8F6] transition-colors duration-500 px-2 md:px-4 -mx-2 md:-mx-4"
                            >
                                <div className="col-span-2 md:col-span-1 editorial-number text-2xl md:text-3xl not-italic">
                                    0{i + 1}
                                </div>
                                <h3 className="col-span-10 md:col-span-4 font-serif text-2xl md:text-4xl font-light leading-tight">
                                    {s.title}
                                </h3>
                                <p className="col-span-12 md:col-span-6 md:col-start-7 text-sm md:text-base text-[#555] leading-relaxed md:leading-relaxed self-center">
                                    {s.text}
                                </p>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-[#0F0F0E] text-white py-24 md:py-32">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-6">
                    <FadeIn className="col-span-12 md:col-span-8 md:col-start-3 text-center">
                        <h2 className="font-serif font-light text-3xl md:text-6xl leading-[1.05] tracking-tight">
                            {t.home.ctaTitle}
                        </h2>
                        <Link
                            to="/kontakt"
                            data-testid="privat-cta"
                            className="inline-flex items-center gap-3 mt-12 px-8 py-4 border border-white/70 hover:bg-white hover:text-[#0F0F0E] transition-colors text-[11px] tracking-luxury uppercase"
                        >
                            {t.privat.cta} <ArrowRight size={14} />
                        </Link>
                    </FadeIn>
                </div>
            </section>
        </main>
    );
};

export default Privat;
